import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  MenuItem,
  Slider,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { DoneState } from '../lib/useDoneState'
import { NotesState } from '../lib/useNotes'
import { TextEditsState } from '../lib/useTextEdits'
import { Voiceover } from '../types'

import { EditableRichText } from './EditableRichText'
import { NoteButton } from './NoteButton'
import { ReadingControls } from './ReadingControls'
import { ResizableReader } from './ResizableReader'

type Props = {
  rows: Voiceover[]
  fontSize: number
  onFontSize: (n: number) => void
  done: DoneState
  notes: NotesState
  textEdits: TextEditsState
}

type Chapter = {
  poiIndex: number
  poiTitle: string
  items: Voiceover[]
}

const MIN_SPEED = 10
const MAX_SPEED = 200

// One chapter = one POI. The speaker can read the whole chapter in a single take
// and cut afterwards; each segment still carries its file id as a quiet marker
// so they know which part is which without breaking the reading flow.
function groupChapters (rows: Voiceover[]): Chapter[] {
  const map = new Map<number, Chapter>()
  rows.forEach((row) => {
    let ch = map.get(row.poiIndex)
    if (!ch) {
      ch = { poiIndex: row.poiIndex, poiTitle: row.poiTitle, items: [] }
      map.set(row.poiIndex, ch)
    }
    ch.items.push(row)
  })
  return Array.from(map.values()).sort((a, b) => a.poiIndex - b.poiIndex)
}

export const VoiceoverChapters: React.FC<Props> = ({
  rows,
  fontSize,
  onFontSize,
  done,
  notes,
  textEdits
}) => {
  const chapters = useMemo(() => groupChapters(rows), [rows])
  const [poiIndex, setPoiIndex] = useState<number>(chapters[0]?.poiIndex ?? 0)

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const accRef = useRef(0)
  const [autoScroll, setAutoScroll] = useState(false)
  const [speed, setSpeed] = useState(40) // px per second

  // Track the scrollable element (ResizableReader owns the width / drag handles).
  const setScrollEl = useCallback((el: HTMLDivElement | null) => {
    scrollRef.current = el
  }, [])

  // Keep a valid selection when the underlying rows (search) change.
  useEffect(() => {
    if (chapters.length > 0 && !chapters.some((c) => c.poiIndex === poiIndex)) {
      setPoiIndex(chapters[0].poiIndex)
    }
  }, [chapters, poiIndex])

  // Start each chapter from the top.
  useEffect(() => {
    accRef.current = 0
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [poiIndex])

  // Smooth auto-scroll. Fractional pixels are accumulated so slow speeds still move.
  useEffect(() => {
    if (!autoScroll) return
    let raf = 0
    let last = performance.now()
    const step = (now: number) => {
      const el = scrollRef.current
      const dt = (now - last) / 1000
      last = now
      if (el) {
        accRef.current += speed * dt
        const whole = Math.floor(accRef.current)
        if (whole >= 1) {
          accRef.current -= whole
          el.scrollTop += whole
        }
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 1) {
          setAutoScroll(false)
          return
        }
      }
      raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [autoScroll, speed])

  // Any manual scroll (wheel / touch / scroll keys) cancels auto-scroll.
  useEffect(() => {
    if (!autoScroll) return
    const el = scrollRef.current
    if (!el) return
    const stop = () => setAutoScroll(false)
    const onKey = (e: KeyboardEvent) => {
      if (
        ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(
          e.key
        )
      ) {
        stop()
      }
    }
    el.addEventListener('wheel', stop, { passive: true })
    el.addEventListener('touchmove', stop, { passive: true })
    window.addEventListener('keydown', onKey)
    return () => {
      el.removeEventListener('wheel', stop)
      el.removeEventListener('touchmove', stop)
      window.removeEventListener('keydown', onKey)
    }
  }, [autoScroll])

  const chapter = chapters.find((c) => c.poiIndex === poiIndex)
  const chapterIds = chapter?.items.map((i) => i.suggestedFileName) ?? []
  const allDone =
    chapterIds.length > 0 && chapterIds.every((id) => done.isDone(id))

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
        <TextField
          select
          size="small"
          label="Kapitel"
          value={chapter ? poiIndex : ''}
          onChange={(e) => setPoiIndex(Number(e.target.value))}
          sx={{ minWidth: 280 }}
        >
          {chapters.map((c) => {
            const chDone = c.items.filter((i) =>
              done.isDone(i.suggestedFileName)
            ).length
            return (
              <MenuItem key={c.poiIndex} value={c.poiIndex}>
                {c.poiIndex}. {c.poiTitle} ({chDone}/{c.items.length})
              </MenuItem>
            )
          })}
        </TextField>

        <Button
          size="small"
          variant={allDone ? 'outlined' : 'contained'}
          startIcon={<DoneAllIcon />}
          disabled={chapterIds.length === 0}
          onClick={() => done.setMany(chapterIds, !allDone)}
        >
          {allDone ? 'Kapitel zurücksetzen' : 'Ganzes Kapitel erledigt'}
        </Button>
      </Box>

      <Divider />

      <ReadingControls
        fontSize={fontSize}
        onFontSize={onFontSize}
        trailing={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip
              title={autoScroll ? 'Autoscroll pausieren' : 'Autoscroll starten'}
            >
              <IconButton
                size="small"
                color={autoScroll ? 'primary' : 'default'}
                onClick={() => setAutoScroll((v) => !v)}
              >
                {autoScroll ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              Tempo
            </Typography>
            <Slider
              size="small"
              min={MIN_SPEED}
              max={MAX_SPEED}
              step={5}
              value={speed}
              onChange={(_, v) => setSpeed(v as number)}
              sx={{ width: 120 }}
            />
          </Box>
        }
      />

      {/* Drag the thick left/right edges to set the reading width. */}
      <ResizableReader scrollRef={setScrollEl}>
        <Box>
          {chapter?.items.map((item, idx) => {
            const prev = chapter.items[idx - 1]
            const showSection = !prev || prev.sectionTitle !== item.sectionTitle
            const isDone = done.isDone(item.suggestedFileName)
            return (
              <Box key={item.suggestedFileName}>
                {showSection
                  ? (
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 3 }}
                    >
                      {item.sectionTitle}
                    </Typography>
                    )
                  : null}

                {/* Quiet marker / cut point: small checkbox + muted file id. */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 3,
                    pt: idx === 0 ? 0 : 1,
                    borderTop: idx === 0 ? 'none' : '1px dashed',
                    borderColor: 'divider'
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isDone}
                    onChange={() => done.toggle(item.suggestedFileName)}
                    sx={{ p: 0.25 }}
                  />
                  <Typography
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.72rem',
                      color: 'text.disabled'
                    }}
                  >
                    {item.suggestedFileName}
                  </Typography>
                  <Tooltip title="Dateiname kopieren">
                    <IconButton
                      size="small"
                      onClick={() =>
                        navigator.clipboard?.writeText(item.suggestedFileName)
                      }
                    >
                      <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                  <NoteButton
                    note={notes.get(item.suggestedFileName)}
                    onChange={(t) => notes.set(item.suggestedFileName, t)}
                  />
                </Box>

                <Box sx={{ mt: 1, opacity: isDone ? 0.45 : 1 }}>
                  <EditableRichText
                    original={item.text}
                    value={textEdits.get(item.suggestedFileName)}
                    onChange={(html) =>
                      textEdits.set(item.suggestedFileName, html)
                    }
                    fontSize={fontSize}
                  />
                </Box>
              </Box>
            )
          })}
        </Box>
      </ResizableReader>
    </Box>
  )
}
